import { useEffect, useRef, useState } from "react";

import { DealPermissionConstants, ProjectGroupPermission, ProjectPermissionConstants } from "../../constants";
import { logError } from "../../service/error";
import { getCurrentUserProjectPermissions } from "../../service/query";
import { getCurrentUserGroupPermission, searchAclEntries } from "../../service/query/QueryService.full";
import { ServiceError, Status } from "../../service/Shared";
/*
  Deep import, not the widget barrel: that re-exports components which import from utils, and closing a
  utils → widget → utils cycle here would need a Storybook stub for the whole hooks folder. Toast's own module
  graph reaches only assets, service/error and rc-notification, so this edge is safe.
*/
import { Toast } from "../../widget/general/Toast";

/*
  The document stores a file can be uploaded to. Deliberately the same union as
  TaskParentPolicy["documents"]["scope"], so the task overlay can hand its parent policy straight to this hook.
*/
export type DocumentUploadScope = "project" | "deal" | "group";

export interface DocumentUploadParent {
  scope: DocumentUploadScope;
  // The uuid the permission is held against: the project, the group (a landholding uuid IS its group uuid), or the deal.
  uuid: string | null | undefined;
}

interface UseDocumentUploadPermissionReturnData {
  canUploadDocument: boolean;
  isDocumentUploadPermissionLoading: boolean;
}

/*
  The outcome of one read, keeping "the store says no" apart from "the store could not be asked". Both withhold
  the upload, but only the second is a fault to report.
*/
type GrantRead = { ok: true; canUpload: boolean } | { ok: false; errors: ServiceError[] | undefined };

// A verdict is only ever true for the parent it was read against — see canUploadDocument below.
interface GrantVerdict {
  key: string;
  canUpload: boolean;
}

/*
  Which grants let a document into each store. The rule rather than the fetch is what the surfaces share: a
  caller already holding the acting org's permissions (from ProjectContext, say) applies it directly instead
  of paying for a second read.
*/
export const DOCUMENT_UPLOAD_PERMISSIONS: Record<DocumentUploadScope, string[]> = {
  project: [ProjectPermissionConstants.MANAGE_PROJECT_DOCUMENT],
  // A landholding's documents are project documents held against the group, so the grant is the same one.
  group: [ProjectPermissionConstants.MANAGE_PROJECT_DOCUMENT],
  deal: [DealPermissionConstants.WRITE_DEAL, DealPermissionConstants.MANAGE_DEAL],
};

export const hasDocumentUploadPermission = (scope: DocumentUploadScope, permissions: string[]): boolean =>
  DOCUMENT_UPLOAD_PERMISSIONS[scope].some((permission) => permissions.includes(permission));

/*
  Whether the acting organisation may upload a document to a parent object — the one place that question is
  answered (KAN-9638). It used to be asked once per surface, and the task overlay never asked at all: it
  offered "Add attachment" to anyone who could edit the task, which is a different right entirely, so users
  without the document grant were shown an upload that the API then refused.

  Each store keeps the grant in its own place, which is why this cannot be a single call:
    - project ⇒ manageProjectDocument, from the project's permission list
    - group   ⇒ manageProjectDocument, as a group access level (a landholding's documents are project
                documents held against the group)
    - deal    ⇒ writeDeal or manageDeal, from the deal's own per-record ACL, granted to a user against that
                one deal rather than to their organisation

  Denies while the answer is in flight: offering the upload and withdrawing it a moment later is the bug this
  fixes, in miniature.
*/
export const useDocumentUploadPermission = (
  parent: DocumentUploadParent | undefined,
  /*
    The signed-in user, which only the deal scope needs — the project and group stores answer for the acting
    organisation without being told who is asking. Passed in rather than read from useAuth: that module
    reaches into widget, and widget imports utils, so calling it from here would close a require cycle.
  */
  currentUserUuid: string | null | undefined
): UseDocumentUploadPermissionReturnData => {
  const [verdict, setVerdict] = useState<GrantVerdict | null>(null);
  const [isDocumentUploadPermissionLoading, setIsDocumentUploadPermissionLoading] = useState(false);
  // Only the newest read may write the verdict — the parent changes as the overlay is reopened on another task.
  const requestIdRef = useRef(0);

  const scope = parent?.scope;
  const uuid = parent?.uuid;
  const parentKey = scope && uuid ? `${scope}:${uuid}` : null;

  /*
    Derived during render rather than reset in the effect. A verdict carries the parent it was read for, so one
    read for the previous parent can never be offered against the current one — not even for the single commit
    between a change of parent and the effect that starts the new read.

    Keyed by parent and NOT by lifecycle, deliberately. A host that hides its panel rather than unmounting it
    (see the `show` gate in useWorkflowTaskPanel) drops the parent and restores it, and on the way back this
    keeps showing the answer already read for that same parent while the fresh read is in flight — stale while
    revalidating, and self-correcting within one round trip, since `parentKey` is a dependency of that read.

    Refusing any answer from before the current open was considered and rejected: it would withhold the offer on
    every reopen of the same task, and TaskPanelLeft hides the whole attachments section while there is nothing
    to list and nothing to add, so the section itself would flicker in each time. What that buys is one query's
    worth of exposure to a grant revoked server-side between one open and the next of the same panel, in the
    same browser session. Nothing else here revalidates permissions mid-session either — ProjectContext reads
    them once on mount — and the API refuses a request the grant does not cover regardless.
  */
  const canUploadDocument = parentKey !== null && verdict?.key === parentKey && verdict.canUpload;

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    /*
      Retires this read when the hook unmounts or the parent changes. Without it a failure landing after the
      consumer has gone still counts as current, and the toast would arrive on whatever screen the user moved
      on to, about an upload they are no longer looking at.
    */
    const retireRead = (): void => {
      requestIdRef.current += 1;
    };

    if (!parentKey || !scope || !uuid) {
      setIsDocumentUploadPermissionLoading(false);
      return retireRead;
    }

    const resolve = async (): Promise<GrantRead> => {
      switch (scope) {
        case "project": {
          const response = await getCurrentUserProjectPermissions({ projectUuids: [uuid], groupUuids: null });

          if (response.status !== Status.Success) return { ok: false, errors: response.errors };

          return {
            ok: true,
            canUpload: hasDocumentUploadPermission("project", response.data?.[0]?.currentUserPermissions ?? []),
          };
        }
        case "group": {
          /*
            The group store answers with an access level for one named permission rather than a list, so this
            scope asks the question the way the landholding documents page already does.
          */
          const response = await getCurrentUserGroupPermission({
            groupUuid: uuid,
            permission: DOCUMENT_UPLOAD_PERMISSIONS.group[0],
          });

          if (response.status !== Status.Success) return { ok: false, errors: response.errors };

          return { ok: true, canUpload: response.data?.accessLevel === ProjectGroupPermission.FULL };
        }
        case "deal": {
          /*
            Granted to a user against the one deal, not to their organisation — a deal names a lead and a team
            of individuals — so the ACL read is scoped to the signed-in user rather than the acting org.
          */
          if (!currentUserUuid) return { ok: true, canUpload: false };

          const response = await searchAclEntries({ resourceUuid: uuid, subjectUuid: currentUserUuid });

          if (response.status !== Status.Success) return { ok: false, errors: response.errors };

          return { ok: true, canUpload: hasDocumentUploadPermission("deal", response.data?.[0]?.permissions ?? []) };
        }
        default:
          return { ok: true, canUpload: false };
      }
    };

    /*
      A failed read is not a grant, so the upload stays hidden rather than being offered for the API to refuse.
      Reported rather than swallowed: silently denying would make an outage look exactly like a permission the
      user does not have. Logged even when stale — a failure is worth diagnosing either way — but only toasted
      for the read the user is waiting on, or moving between parents during an outage would stack up messages
      for parents no longer on screen.
    */
    const reportFailure = (error: Error | string): void => {
      logError({ error });
      if (requestId !== requestIdRef.current) return;
      setVerdict({ key: parentKey, canUpload: false });
      Toast.error({ message: "Upload permissions could not be checked. Please try again." });
    };

    setIsDocumentUploadPermissionLoading(true);

    resolve()
      .then((read) => {
        /*
          The service wrappers resolve an error response instead of rejecting, so a failed read arrives here
          rather than in the catch below. Treating it as `false` would be the silent denial this guards against.
        */
        if (!read.ok) {
          reportFailure(`Could not read the ${scope} document upload grant: ${JSON.stringify(read.errors ?? [])}`);
          return;
        }

        if (requestId !== requestIdRef.current) return;
        setVerdict({ key: parentKey, canUpload: read.canUpload });
      })
      // Still needed: a wrapper could reject on something it does not model, and an unhandled one would surface
      // as a console error with the upload silently withheld.
      .catch((error) => reportFailure(error))
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsDocumentUploadPermissionLoading(false);
      });

    return retireRead;
  }, [parentKey, scope, uuid, currentUserUuid]);

  return { canUploadDocument, isDocumentUploadPermissionLoading };
};
